import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type HorecaShiftPlanDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const inputCls = "h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none";
const DAYS = ["Pon", "Uto", "Sre", "Čet", "Pet", "Sub", "Ned"];

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (s: string, n: number) => {
  const d = new Date(`${s}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
};
const mondayOf = (s: string) => {
  const d = new Date(`${s}T12:00:00Z`);
  return addDays(s, -((d.getUTCDay() + 6) % 7));
};
const todayBg = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Belgrade" });
const short = (s: string) => s.split("-").reverse().slice(0, 2).join(".") + ".";
const hours = (m: number) => `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, "0") : ""}`;

function errMsg(e: unknown) {
  if (e instanceof ApiError) {
    const m = e.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return "Radnja nije uspela.";
}

export function RasporedTab() {
  const qc = useQueryClient();
  const [week, setWeek] = useState(mondayOf(todayBg()));
  const [edit, setEdit] = useState<{ user_id: string; name: string; date: string; start: string; end: string; note: string; has: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const staff = useQuery({ queryKey: ["horeca-staff"], queryFn: () => api.getHorecaStaff(), retry: false });
  const plans = useQuery({ queryKey: ["horeca-schedule", week], queryFn: () => api.getHorecaSchedule({ from: days[0] as string, to: days[6] as string }), retry: false });
  const refresh = () => { setEdit(null); setErr(null); qc.invalidateQueries({ queryKey: ["horeca-schedule"] }); };
  const save = useMutation({
    mutationFn: (e: NonNullable<typeof edit>) => api.saveHorecaSchedule({ user_id: e.user_id, date: e.date, start_time: e.start, end_time: e.end, note: e.note || null }),
    onSuccess: refresh,
    onError: (e) => setErr(errMsg(e)),
  });
  const del = useMutation({ mutationFn: (e: NonNullable<typeof edit>) => api.deleteHorecaSchedule(e.user_id, e.date), onSuccess: refresh, onError: (e) => setErr(errMsg(e)) });

  const workers = Array.isArray(staff.data) ? staff.data : ((staff.data as { staff?: typeof staff.data } | undefined)?.staff ?? []);
  const find = (u: string, d: string): HorecaShiftPlanDto | undefined => plans.data?.plans.find((p) => p.user_id === u && p.date === d);
  const total = (u: string) => (plans.data?.plans ?? []).filter((p) => p.user_id === u).reduce((a, p) => a + p.minutes, 0);

  if (staff.isLoading || plans.isLoading) return <Skeleton className="h-32 w-full" />;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Kliknite na polje da upišete smenu radniku (npr. 08:00–16:00). Radnik vidi svoj raspored na kasi i telefonu.</p>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
      <div className="flex items-center gap-2">
        <button className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setWeek(addDays(week, -7))}>←</button>
        <span className="text-sm font-medium">{short(days[0] as string)} – {short(days[6] as string)}</span>
        <button className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setWeek(addDays(week, 7))}>→</button>
        <button className="rounded-lg px-3 py-1.5 text-sm hover:bg-slate-100" onClick={() => setWeek(mondayOf(todayBg()))}>Ova nedelja</button>
      </div>

      {edit && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-3 shadow-subtle">
          <div className="text-sm font-semibold">{edit.name} · {short(edit.date)}</div>
          <label className="text-xs text-slate-500">Od<input type="time" className={inputCls + " block"} value={edit.start} onChange={(e) => setEdit({ ...edit, start: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Do<input type="time" className={inputCls + " block"} value={edit.end} onChange={(e) => setEdit({ ...edit, end: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Napomena<input className={inputCls + " block w-48"} value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} /></label>
          <Button disabled={save.isPending} onClick={() => save.mutate(edit)}>Sačuvaj</Button>
          {edit.has && <button className="rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50" onClick={() => del.mutate(edit)}>Obriši</button>}
          <button className="rounded-lg px-3 py-2 text-sm hover:bg-slate-100" onClick={() => setEdit(null)}>Otkaži</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-xs text-slate-500">
              <th className="px-3 py-2 text-left font-medium">Radnik</th>
              {days.map((d, i) => <th key={d} className={"px-2 py-2 font-medium " + (d === todayBg() ? "text-brand" : "")}>{DAYS[i]} {short(d)}</th>)}
              <th className="px-3 py-2 text-right font-medium">Ukupno</th>
            </tr>
          </thead>
          <tbody>
            {workers.map((w) => (
              <tr key={w.user_id} className="border-b border-border last:border-0">
                <td className="px-3 py-1.5 font-medium">{w.name}</td>
                {days.map((d) => {
                  const p = find(w.user_id, d);
                  return (
                    <td key={d} className="px-1 py-1 text-center">
                      <button
                        className={"w-full rounded-lg px-1 py-1.5 text-xs " + (p ? "bg-brand/10 text-slate-800 hover:bg-brand/20" : "text-slate-300 hover:bg-slate-50")}
                        onClick={() => setEdit({ user_id: w.user_id, name: w.name, date: d, start: p?.start_time ?? "08:00", end: p?.end_time ?? "16:00", note: p?.note ?? "", has: !!p })}
                      >
                        {p ? `${p.start_time}–${p.end_time}` : "+"}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 text-right tabular-nums">{hours(total(w.user_id))}</td>
              </tr>
            ))}
            {workers.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Nema radnika.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
